import { AuthResponse, JWTS } from "@/interfaces/auth";
import BaseApiClient from "@/api/base";
import store from "@/store";

export class AuthApiClient extends BaseApiClient {
  /* Authentication Requests */
  public signup(username: string, password: string): Promise<AuthResponse> {
    return this.post("/auth/signup", {
      username,
      password
    });
  }

  public login(username: string, password: string): Promise<AuthResponse> {
    // Login and save the JWTs
    return new Promise<AuthResponse>((resolve, reject) => {
      this.post("/auth/login", {
        username,
        password
      })
        .then(result => {
          const response: AuthResponse = result as AuthResponse;
          if (response.ok) {
            console.log("Successful login.");

            const auth = {
              name: username,
              email: username,
              jwts: response.jwts
            };

            store.commit("login", auth);

            resolve(response);
          }
        })
        .catch(error => reject(error));
    });
  }

  public refresh(jwts: JWTS): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      this.post("/auth/refresh", jwts)
        .then(result => {
          const response: AuthResponse = result as AuthResponse;
          if (response.ok) {
            console.log("Successful JWT refresh.");

            const auth = store.state.auth;
            auth.jwts = response.jwts;

            store.commit("refreshLogin", response.jwts);

            // If the JWTs were previously stored in local storage, refresh them.
            const storedAuth = localStorage.auth
              ? JSON.parse(localStorage.auth)
              : null;
            if (storedAuth) {
              localStorage.auth = JSON.stringify(auth);
            }

            resolve(response);
          }
        })
        .catch(error => reject(error));
    });
  }

  public logOut(): Promise<void> {
    return new Promise<void>(resolve => {
      localStorage.removeItem("auth");
      store.commit("logout");
      resolve();
    });
  }

  public activate(username: string, code: string): Promise<AuthResponse> {
    return this.post("/auth/activate_user", {
      username,
      code
    });
  }

  public sendNewCode(username: string): Promise<AuthResponse> {
    return this.post("/auth/resend_code", {
      username
    });
  }

  public resetPassword(username: string): Promise<AuthResponse> {
    return this.post("/auth/forgot_password", {
      username
    });
  }

  public confirmResetPassword(
    username: string,
    password: string,
    code: string
  ): Promise<AuthResponse> {
    return this.post("/auth/confirm_forgot_password", {
      username,
      password,
      code
    });
  }

  protected post<AuthResponse>(
    endpoint: string,
    payload: any
  ): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      super
        .post<AuthResponse>(endpoint, payload, undefined, [400, 422])
        .then((result: AuthResponse) => resolve(result))
        .catch(error => {
          if (error.response.status === 422) {
            reject("auth.errors.invalidInput");
          } else if (!error.response.data.errors) {
            reject("api.serverError");
          } else if (error.response.data.errors.user_does_not_exist) {
            reject("auth.errors.userUnknown");
          } else if (error.response.data.errors.authentication_failed) {
            reject("auth.errors.authFailed");
          } else if (error.response.data.errors.user_not_confirmed) {
            reject("auth.errors.userNotConfirmed");
          } else if (error.response.data.errors.user_already_exists) {
            reject("auth.errors.userExists");
          } else if (error.response.data.errors.password_is_insecure) {
            reject("auth.errors.weakPassword");
          } else if (error.response.data.message) {
            reject(error.response.data.message);
          } else {
            reject("auth.errors.unknown");
          }
        });
    });
  }
}
